import { Client, type IMessage, type StompSubscription } from '@stomp/stompjs';
import SockJS from 'sockjs-client';
import { getApiBaseUrl } from '@/config/env';
import type { RealtimeConnectionStatus, RealtimeMessage } from './types';

export type RealtimeMessageHandler = (message: RealtimeMessage) => void;

type Subscription = {
  destination: string;
  handler: RealtimeMessageHandler;
};

/**
 * STOMP over SockJS — connects to Spring {@code /api/ws} with JWT in CONNECT headers.
 */
export class RealtimeClient {
  private client: Client | null = null;
  private status: RealtimeConnectionStatus = 'idle';
  private subscriptions = new Map<string, Subscription>();
  private stompSubscriptions = new Map<string, StompSubscription>();
  private reconnectAttempt = 0;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private connectToken: string | null = null;
  private onStatusChange: ((status: RealtimeConnectionStatus) => void) | null = null;

  get connectionStatus(): RealtimeConnectionStatus {
    return this.status;
  }

  setStatusListener(listener: ((status: RealtimeConnectionStatus) => void) | null): void {
    this.onStatusChange = listener;
  }

  private setStatus(status: RealtimeConnectionStatus): void {
    this.status = status;
    this.onStatusChange?.(status);
  }

  /** Connect with JWT — STOMP CONNECT header Authorization: Bearer {token} */
  async connect(token: string): Promise<void> {
    this.connectToken = token;
    if (this.client?.active) {
      this.client.deactivate();
    }

    this.setStatus('connecting');
    const wsUrl = `${getApiBaseUrl()}/ws`;

    this.client = new Client({
      webSocketFactory: () => new SockJS(wsUrl) as unknown as WebSocket,
      connectHeaders: { Authorization: `Bearer ${token}` },
      reconnectDelay: 0,
      heartbeatIncoming: 20_000,
      heartbeatOutgoing: 20_000,
      onConnect: () => {
        this.reconnectAttempt = 0;
        this.setStatus('connected');
        this.resubscribeAll();
      },
      onStompError: () => {
        this.setStatus('disconnected');
        if (this.connectToken) {
          this.scheduleReconnect(() => this.connect(this.connectToken!));
        }
      },
      onWebSocketClose: () => {
        if (this.status !== 'idle') {
          this.setStatus('disconnected');
          if (this.connectToken) {
            this.scheduleReconnect(() => this.connect(this.connectToken!));
          }
        }
      },
    });

    this.client.activate();
  }

  disconnect(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.connectToken = null;
    this.stompSubscriptions.forEach((sub) => sub.unsubscribe());
    this.stompSubscriptions.clear();
    this.subscriptions.clear();
    if (this.client?.active) {
      this.client.deactivate();
    }
    this.client = null;
    this.setStatus('idle');
  }

  subscribe(destination: string, handler: RealtimeMessageHandler): () => void {
    this.subscriptions.set(destination, { destination, handler });
    if (this.client?.connected) {
      this.activateSubscription(destination, handler);
    }
    return () => {
      this.subscriptions.delete(destination);
      this.stompSubscriptions.get(destination)?.unsubscribe();
      this.stompSubscriptions.delete(destination);
    };
  }

  /** Exponential backoff reconnect — max 30s with jitter */
  scheduleReconnect(connectFn: () => Promise<void>): void {
    if (this.reconnectTimer) return;
    const delay = Math.min(30_000, 1000 * 2 ** this.reconnectAttempt);
    const jitter = delay * (0.8 + Math.random() * 0.4);
    this.reconnectAttempt += 1;
    this.setStatus(this.reconnectAttempt >= 3 ? 'degraded' : 'disconnected');
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = null;
      void connectFn();
    }, jitter);
  }

  private resubscribeAll(): void {
    this.stompSubscriptions.forEach((sub) => sub.unsubscribe());
    this.stompSubscriptions.clear();
    for (const { destination, handler } of this.subscriptions.values()) {
      this.activateSubscription(destination, handler);
    }
  }

  private activateSubscription(destination: string, handler: RealtimeMessageHandler): void {
    if (!this.client?.connected) return;
    const stompSub = this.client.subscribe(destination, (message: IMessage) => {
      try {
        const body = JSON.parse(message.body) as RealtimeMessage;
        handler(body);
      } catch {
        // ignore malformed payloads
      }
    });
    this.stompSubscriptions.set(destination, stompSub);
  }
}

export const realtimeClient = new RealtimeClient();
